import { Color } from '@persona-id/design-tokens';
import { ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { InquiryFunnelStepType } from 'dashboard/models/inquiry-funnel';
import { Z_INDICES } from 'dashboard/styles/variables';
import Sticker from 'design-system/components/Sticker';
import { Label } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import IconUser from 'images/icons/user.svg';
import IconWorkflows from 'images/icons/workflows.svg';

export const WORKFLOW_BRANCH_HEIGHT = 256;
export const WORKFLOW_STEP_CARD_WIDTH = 256;
export const WORKFLOW_STEP_CARD_HEIGHT = 100;
export const CARD_BORDER_WIDTH = 2;

const StyledArrow = styled(ArrowRightIcon)`
  margin-top: auto;
  margin-left: auto;
  color: ${Color.CorePurple700};
  display: flex;
  flex-direction: row;
`;

const CardBase = styled.div`
  position: relative;
`;

const StickerContainer = styled.div`
  position: absolute;
  top: -32px;
`;

const DropOffContainer = styled.div`
  position: absolute;
  top: -56px;
`;

const Card = styled.div<{
  active: boolean;
  width?: number;
  isPlaceholder?: boolean;
  highlight?: boolean;
  terminal: boolean;
}>`
  position: relative;
  display: flex;
  width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  height: ${WORKFLOW_STEP_CARD_HEIGHT}px;
  border-width: ${({ active }) => (active ? CARD_BORDER_WIDTH * 1.75 : CARD_BORDER_WIDTH)}px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  box-shadow: ${Elevation.Mid};
  padding: 14px;
  background-color: ${({ highlight }) => (highlight ? Color.CorePurple100 : Color.PrimaryWhite)};
  box-sizing: border-box;
  cursor: pointer;
  z-index: ${Z_INDICES.withShadow};
  user-select: none;
  border-color: ${({ active, terminal }) => {
    if (active) {
      return Color.CorePurple800;
    } else if (!terminal) {
      return Color.CoreSlate700;
    } else {
      return Color.CorePurple600;
    }
  }};
`;

const Text = styled('div')`
  overflow-wrap: break-word;
  overflow: hidden;
  line-height: 16px;
  max-height: 48px;
`;

const Name = styled(Label)`
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropOffTextContainer = styled.span`
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

const MetricsText = styled('div')<{ isTerminal: boolean; active: boolean }>`
  position: absolute;
  bottom: 10px;
  color: ${({ isTerminal, active }) =>
    isTerminal || active ? Color.CorePurple700 : Color.CoreSlate700};
`;

const StyledIconUser = styled(IconUser)`
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

interface InquiryFunnelStepCardProps {
  id?: string;
  dataTest?: string;
  name: string;
  condition?: {
    name: string;
  };
  dropOffMetricsText?: string;
  type: InquiryFunnelStepType;
  width?: number;
  focused: boolean;
  token: string;
  isTerminal: boolean;
  active: boolean;
  metricText: string;
}

/**
 * This component is responsible for rendering an individual workflow step or
 * trigger.
 */
export const InquiryFunnelStepCard = forwardRef<HTMLDivElement, InquiryFunnelStepCardProps>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (
    {
      id,
      dataTest,
      name,
      active,
      width,
      focused,
      token,
      isTerminal,
      metricText,
      condition,
      dropOffMetricsText,
    }: InquiryFunnelStepCardProps,
    forwardedRef: (instance: HTMLDivElement) => void,
  ) => (
    <CardBase>
      {condition != null && (
        <StickerContainer>
          <Sticker icon={<IconWorkflows />}>{condition.name}</Sticker>
        </StickerContainer>
      )}
      {dropOffMetricsText != null && (
        <DropOffContainer>
          <Sticker disabled={true} variant='pink' icon={<StyledIconUser />}>
            <DropOffTextContainer>{dropOffMetricsText}</DropOffTextContainer>
          </Sticker>
        </DropOffContainer>
      )}
      <Card
        id={id}
        active={active}
        highlight={!active && focused}
        width={width}
        ref={forwardedRef}
        terminal={isTerminal}
        data-test={dataTest ?? `workflow-step-card__${token}`}
      >
        <Text title={name}>
          <Name size='sm'>{name}</Name>
          <MetricsText active={active} isTerminal={isTerminal}>
            {metricText}
          </MetricsText>
        </Text>
        {isTerminal && <StyledArrow />}
      </Card>
    </CardBase>
  ),
);

InquiryFunnelStepCard.displayName = 'InquiryFunnelStepCard';
